// Alerts (doc 03 §3.7). One stream for conditions the control plane raises and
// anything Alertmanager forwards.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type Alert, type Me, type Page } from "../api";
import { Badge, Card, Empty, ErrorNote, PageHeader, Spinner, formatTime, severityTone, useAction } from "../ui";

export default function Alerts({ me }: { me: Me }) {
  const [severity, setSeverity] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const queryClient = useQueryClient();
  const { run } = useAction();

  const params = new URLSearchParams();
  if (severity) params.set("severity", severity);
  if (showResolved) params.set("state", "resolved");

  const { data, isLoading, error } = useQuery<Page<Alert>>({
    queryKey: ["alerts", severity, showResolved],
    queryFn: () => api.get(`/v1/alerts?${params}`),
    refetchInterval: 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["alerts"] });

  return (
    <>
      <PageHeader title="Alerts" subtitle="P1 pages, P2 emails, P3 batches into a digest." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select className="input max-w-[10rem]" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="">All severities</option>
          <option value="P1">P1 — page</option>
          <option value="P2">P2 — email</option>
          <option value="P3">P3 — digest</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input type="checkbox" className="accent-brand" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show resolved
        </label>
      </div>

      <Card padded={false}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4"><ErrorNote error={error} /></div>
        ) : !data?.items.length ? (
          <Empty>{showResolved ? "No resolved alerts." : "Nothing is firing. "}</Empty>
        ) : (
          <ul className="divide-y divide-ink-700">
            {data.items.map((alert) => (
              <li key={alert.id} className="flex flex-wrap items-start gap-4 px-4 py-3">
                <Badge tone={severityTone(alert.severity)}>{alert.severity}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink-100">{alert.summary}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-400">
                    <span className="font-mono">{alert.key}</span>
                    {alert.service_tag && (
                      <Link to={`/endpoints/${alert.endpoint_id}`} className="service-tag hover:underline">
                        {alert.service_tag}
                      </Link>
                    )}
                    <span>first seen {formatTime(alert.first_seen_at)}</span>
                    <span>last seen {formatTime(alert.last_seen_at)}</span>
                    {alert.count > 1 && <span>×{alert.count}</span>}
                    {alert.acknowledged_by && <span>acknowledged by {alert.acknowledged_by}</span>}
                  </div>
                </div>
                {me.role !== "reseller" && alert.state !== "resolved" && (
                  <div className="flex gap-2">
                    {alert.state === "firing" && (
                      <button
                        className="btn-secondary"
                        onClick={() => run(async () => { await api.post(`/v1/alerts/${alert.id}/acknowledge`); refresh(); }, "Acknowledged")}
                      >
                        Acknowledge
                      </button>
                    )}
                    <button
                      className="btn-ghost"
                      onClick={() => run(async () => { await api.post(`/v1/alerts/${alert.id}/resolve`); refresh(); }, "Resolved")}
                    >
                      Resolve
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
